import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Send, Loader2 } from "lucide-react";
import Reveal from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BUDGETS = ["Moins de 2 000 $", "2 000 – 5 000 $", "5 000 – 10 000 $", "Plus de 10 000 $"];

const EMPTY = { name: "", email: "", budget: "", message: "" };

const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) => {
    setForm({ ...form, [key]: e.target.value });
    setErrors({ ...errors, [key]: undefined });
  };

  const validate = () => {
    const errs = {};
    if (form.name.trim().length < 2) errs.name = "Veuillez indiquer votre nom.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Adresse e-mail invalide.";
    if (!form.budget) errs.budget = "Sélectionnez un budget.";
    if (form.message.trim().length < 10) errs.message = "Votre message est un peu court (10 caractères min.).";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message envoyé ! Je vous réponds sous 24h.");
      setForm(EMPTY);
    } catch {
      toast.error("Une erreur est survenue. Réessayez dans un instant.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass = (key) =>
    `w-full bg-[#111115] border rounded-xl px-5 py-4 text-sm text-white placeholder:text-zinc-600 outline-none transition-colors duration-300 focus:border-blue-500 ${
      errors[key] ? "border-red-500/60" : "border-white/10"
    }`;

  return (
    <section id="contact" className="py-24 lg:py-32 bg-[#0b0b0f]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-blue-400 mb-4">Contact</p>
              <h2 className="font-display font-bold tracking-tight text-2xl sm:text-4xl lg:text-5xl mb-6">
                Donnons vie à<br />votre <span className="text-blue-500">projet</span>
              </h2>
              <p className="text-zinc-400 leading-relaxed mb-10 max-w-md">
                Un site vitrine, une boutique en ligne ou une expérience immersive ?
                Racontez-moi votre idée, je m'occupe du reste.
              </p>
              <div className="flex flex-col gap-5">
                <a
                  href="mailto:contact@merdi.me"
                  className="flex items-center gap-4 text-zinc-300 hover:text-white transition-colors duration-300"
                  data-testid="contact-email-link"
                >
                  <span className="w-11 h-11 rounded-xl bg-[#111115] border border-white/10 flex items-center justify-center">
                    <Mail size={18} className="text-blue-400" />
                  </span>
                  contact@merdi.me
                </a>
                <div className="flex items-center gap-4 text-zinc-300" data-testid="contact-location">
                  <span className="w-11 h-11 rounded-xl bg-[#111115] border border-white/10 flex items-center justify-center">
                    <MapPin size={18} className="text-blue-400" />
                  </span>
                  Québec, Canada — travaille à distance
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              <form
                onSubmit={submit}
                className="rounded-2xl border border-white/10 bg-[#111115]/60 p-6 sm:p-10 backdrop-blur"
                data-testid="contact-form"
                noValidate
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                  <div>
                    <input
                      type="text"
                      placeholder="Votre nom"
                      value={form.name}
                      onChange={set("name")}
                      className={inputClass("name")}
                      data-testid="contact-name-input"
                    />
                    {errors.name && <p className="text-red-400 text-xs mt-2">{errors.name}</p>}
                  </div>
                  <div>
                    <input
                      type="email"
                      placeholder="Votre e-mail"
                      value={form.email}
                      onChange={set("email")}
                      className={inputClass("email")}
                      data-testid="contact-email-input"
                    />
                    {errors.email && <p className="text-red-400 text-xs mt-2">{errors.email}</p>}
                  </div>
                </div>

                <div className="mb-5">
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">Budget estimé</p>
                  <div className="flex flex-wrap gap-3" data-testid="contact-budget-group">
                    {BUDGETS.map((b) => (
                      <button
                        type="button"
                        key={b}
                        onClick={() => {
                          setForm({ ...form, budget: b });
                          setErrors({ ...errors, budget: undefined });
                        }}
                        className={`px-4 py-2.5 rounded-full text-sm border transition-colors duration-300 ${
                          form.budget === b
                            ? "bg-blue-500 border-blue-500 text-white"
                            : "border-white/10 text-zinc-400 hover:border-blue-500/50 hover:text-white"
                        }`}
                        data-testid={`budget-option-${BUDGETS.indexOf(b)}`}
                      >
                        {b}
                      </button>
                    ))}
                  </div>
                  {errors.budget && <p className="text-red-400 text-xs mt-2">{errors.budget}</p>}
                </div>

                <div className="mb-8">
                  <textarea
                    rows={5}
                    placeholder="Parlez-moi de votre projet..."
                    value={form.message}
                    onChange={set("message")}
                    className={`${inputClass("message")} resize-none`}
                    data-testid="contact-message-input"
                  />
                  {errors.message && <p className="text-red-400 text-xs mt-2">{errors.message}</p>}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="group w-full sm:w-auto flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 disabled:opacity-60 text-white font-semibold px-8 py-4 rounded-full transition-colors duration-300 shadow-[0_0_50px_-12px_rgba(59,130,246,0.5)]"
                  data-testid="contact-submit-button"
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" /> Envoi en cours...
                    </>
                  ) : (
                    <>
                      Envoyer le message
                      <Send size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                    </>
                  )}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
